import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { interval, Observable, Subject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { MatRadioChange } from '@angular/material/radio';
import { UserAutocompleteComponent } from '@input/user-autocomplete/user-autocomplete.component';
import { isAdminType, StateService } from '@services/state.service';
import { UrlTree } from '@angular/router';
import { DataService } from '@data/data.service';
import { NotificationService } from '@notification/notification.service';
import { ToolbarService } from '@toolbar/toolbar.service';

import { TeamService } from '../../teams/team.service';
import { InputComponent, InputFocusOut } from '@input/input/input.component';
import { DatePickerComponent } from '@input/date-picker/date-picker.component';
import {
  callFuncWith,
  firstArray,
  floatToShortString,
  isFunction,
  isJsObject,
  isNonEmptyString,
  parseDec,
  parseFloatValueOrNull,
  unsub,
  ValidationError,
} from '@util/utils';
import { ErrorElemContainer } from '@util/error-elem-container';
import { OrderInputSizes, OrderType } from '../order-basic';
import { numberToTime, setNumberToTimeIfInteger, strToDate, tryParseTime } from '@util/date-utils';
import { CustomerService } from '../../customers/customer.service';
import { NavigationService } from '@services/navigation.service';
import {
  OrderDataHandler,
  OrderSaveParams,
  OrderSaveResult,
} from '../order-data-handler';
import { EditOrdersComponentData } from './edit-orders.component.data';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { UserDeviceService } from '@services/user-device.service';
import { setMainElementSize } from '@util/visual-utils';

import { getClientRect } from '@util/web-utils';
import { CloseOrderDialogComponent } from '../close-order-dialog/close-order-dialog.component';
import { subClosed } from '@util/angular.utils';
import { entryHasId, tryNoDecimals } from '@util/input-utils';
import { OrderDialogService } from '../order-dialog.service';

const noTeam = {
  id: 0,
  name: '--- kein Team ---',
};

function ne(elem?: ElementRef) {
  return elem?.nativeElement;
}

function setDateFields(content: any, dateFields: string[]) {
  for (let field in dateFields) {
    var val = (content as any)[field] as any;
    if (isNonEmptyString(val)) {
      let d = strToDate(val);
      if (d) {
        (content as any)[field] = d;
      }
    }
  }
}

const unknownSaveErrors = ['Unbekannter Fehler beim Speichern'];

@Component({
  selector: 'app-edit-orders',
  templateUrl: './edit-orders.component.html',
})
export class EditOrdersComponent
  extends EditOrdersComponentData
  implements OnInit, OrderInputSizes {

  elSub?: Subscription | null = null;
  cancelClicked = false;
  canFillOrDelete = false;
  canSave = false;
  canRestore = false;
  dataLoaded = false;

  clearElSub() {
    this.elSub = unsub(this.elSub);
  }

  setCanFillOrDelete(can: boolean) {
    this.canFillOrDelete = can;
  }

  @ViewChild('mainContent') set mainContent(wrap: ElementRef) {
    this._mainWrapper = wrap;
    wrap && this.updateHeights(0);
  }

  createPosWatcher(checkFn: () => boolean) {
    return interval(50).pipe(
      first((value: number) => {
        return checkFn();
      })
    );
  }

  needAutoSave() {
    return this.dataLoaded && this.canSave && !this.data.deleted;
  }

  updateHeights(tabIdx: number) {
    let getMW = () => {
      return this._mainWrapper;
    };

    if (this.tabIdx === tabIdx) {
      if (tabIdx === 0) {
        this.clearElSub();

        this.elSub = this.createPosWatcher(() => {
          const r = getClientRect(ne(this._mainWrapper));
          return !!r && r.top > 0;
        }).subscribe(() => {
          setMainElementSize(ne(getMW()), 32);
          this.elSub = unsub(this.elSub, null);
        });

        //Probleme beim Scrollen, wenn die Seite für den Auftrag
        //neugeladen wird, dann ist die größe nicht gesetzt.
        setTimeout(() => {
          let mw = getMW();
          mw && setMainElementSize(ne(mw), 32);
        }, 400);
      }
    }
  }

  @ViewChild('customerRef', {
    read: UserAutocompleteComponent,
  })
  set customerRef(customer: UserAutocompleteComponent) {
    this._customerRef = customer;
  }

  @ViewChild('halter', {
    read: InputComponent,
  })
  set halter(halter: InputComponent) {
    this._halter = halter;
  }

  @ViewChild('partnerRef', {
    read: UserAutocompleteComponent,
  })
  set partnerRef(partner: UserAutocompleteComponent) {
    this._partnerRef = partner;
  }

  @ViewChild('plate', {
    read: InputComponent,
  })
  set plate(plate: InputComponent) {
    this._plate = plate;
  }

  @ViewChild('km', {
    read: InputComponent,
  })
  set km(km: InputComponent) {
    this._km = km;
  }

  @ViewChild('kind', {
    read: InputComponent,
  })
  set kind(kind: InputComponent) {
    this._kind = kind;
  }

  @ViewChild('firstReg', {
    read: DatePickerComponent,
  })
  set firstReg(firstReg: DatePickerComponent) {
    this._firstReg = firstReg;
  }

  @ViewChild('carDesc', {
    read: InputComponent,
  })
  set carDesc(carDesc: InputComponent) {
    this._carDesc = carDesc;
  }

  @ViewChild('damageDay', {
    read: DatePickerComponent,
  })
  set damageDay(damageDay: DatePickerComponent) {
    this._damageDay = damageDay;
  }

  @ViewChild('damageLoc', {
    read: InputComponent,
  })
  set damageLoc(damageLoc: InputComponent) {
    this._damageLoc = damageLoc;
  }

  @ViewChild('damageNr', {
    read: InputComponent,
  })
  set damageNr(damageNr: InputComponent) {
    this._damageNr = damageNr;
  }

  @ViewChild('insurance', {
    read: InputComponent,
  })
  set insurance(insurance: InputComponent) {
    this._insurance = insurance;
  }

  @ViewChild('deductible', {
    read: InputComponent,
  })
  set deductible(deductible: InputComponent) {
    this._deductible = deductible;
  }

  @ViewChild('sbcustomer', {
    read: InputComponent,
  })
  set sbcustomer(deductible: InputComponent) {
    this._deductible_customer_part = deductible;
  }

  @ViewChild('workDay', {
    read: DatePickerComponent,
  })
  set workDay(workDay: DatePickerComponent) {
    this._workDay = workDay;
  }

  @ViewChild('damage', {
    read: InputComponent,
  })
  set damage(damage: InputComponent) {
    this._damage = damage;
  }

  @ViewChild('montageAddress', {
    read: InputComponent,
  })
  set montageAddress(montageAddress: InputComponent) {
    this._montageAddress = montageAddress;
  }

  @ViewChild('montageCity', {
    read: InputComponent,
  })
  set montageCity(montageCity: InputComponent) {
    this._montageCity = montageCity;
  }

  @ViewChild('montageZip', {
    read: InputComponent,
  })
  set montageZip(montageZip: InputComponent) {
    this._montageZip = montageZip;
  }

  changeTab(idx: number) {
    this.currentTabIdx = idx;
    this.tabSelectedIdx(idx);
  }

  constructor(
    private currentRoute: ActivatedRoute,
    private customerService: CustomerService,
    private dataService: DataService,
    private navService: NavigationService,
    public notificationService: NotificationService,
    private stateService: StateService,
    private teamService: TeamService,
    private toolbarService: ToolbarService,
    private orderDialogService: OrderDialogService,
    private matDialog: MatDialog,
    private userDeviceService: UserDeviceService
  ) {
    super();
  }

  setInitialTab() {
    if (this.isAdmin !== this.lastIsAdmin) {
      !this.isAdmin && this.changeTab(0);
      this.lastIsAdmin = this.isAdmin;
    }
  }

  ngOnInit(): void {
    this.initSizes(this.stateService);

    this.deviceSub = this.userDeviceService.change().subscribe((ev: any) => {
      this.updateHeights(0);
    });

    this.toolbarService.setTitle('Auftrag bearbeiten');

    const teams = this.stateService.getTeams();

    this.teams = [noTeam].concat(teams);

    this.timesSub = this.timesFC.valueChanges.subscribe((val: any) => {
      this.timesValue = val;
    });

    this.stateSub = this.stateService
      .teamsChange()
      .subscribe((change: any[]) => {
        if (Array.isArray(change)) {
          this.teams = [noTeam].concat(change);
        }
      });

    this.isAdmin = this.stateService.isAdmin();

    this.setInitialTab();
    this.setCanFillOrDelete(!!this.isAdmin);

    this.stateService.adminChange().subscribe((isAdmin: isAdminType) => {
      this.isAdmin = isAdmin;
      this.setCanFillOrDelete(!!this.isAdmin);
      this.setInitialTab();
    });

    this.routeSub = this.currentRoute.params.subscribe((param: Params) => {
      let id: any;
      if (param.hasOwnProperty('id')) {
        id = parseDec(param.id);
      }
      if (Number.isFinite(id)) {
        this.id = id;
      }
      this.canSave = this.canSave || (0 === this.id);

      this.getData();
    });
  }

  onStatusChange(entry: any) {
    if (entryHasId(entry)) {
      this.data.status = entry.id;
    }
  }

  onSbTypeChange(entry: any) {
    if (entryHasId(entry)) {
      this.data.deductible_type = entry.id;
      this.onDeductibleFocusOut(null, this.data.deductible_type);
    }
  }

  onDedStatusSelect(entry: any) {
    if (entryHasId(entry)) {
      this.data.deductible_customer_status = entry.id;
    }
  }

  onChangeTeam(teamEntry: any) {
    console.log('Change:team:', teamEntry);
    if (entryHasId(teamEntry)) {
      if (Number.isFinite(teamEntry.id)) {
        this.teamId = teamEntry.id;
        return;
      }

      if (teamEntry.id && typeof teamEntry.id === 'string') {
        const id = parseDec(teamEntry.id);
        if (!isNaN(id)) {
          this.teamId = id;
        }
      }
    }
  }

  onDeductibleFocusOut($event: InputFocusOut | null, type: any = null) {
    var dv = parseFloatValueOrNull(this.data.deductible),
      cv = parseFloatValueOrNull(this.data.deductible_customer_part);
    var msg = '', diff: any,
      assign = (Number.isFinite(dv) && Number.isFinite(cv) && Math.abs(cv - dv) >= 0.001);

    assign = assign || (Number.isFinite(dv) && !Number.isFinite(cv));
    assign = assign || (!Number.isFinite(dv) && Number.isFinite(cv));

    //console.log('S:', assign, dv, cv, this.data.deductible_type, this.data.deductible);
    //Partner
    if (1 === this.data.deductible_type) {
      if (dv > 150) {
        diff = dv - 150;
        if (assign || Math.abs(diff - cv) >= 0.001) {
          diff = tryNoDecimals(diff);
          msg = 'Möchten Sie den SB-Betrag Kunde ' + diff + '€ setzen?'
        }
      } else if (Number.isFinite(cv)) {
        msg = 'Möchten Sie den SB-Betrag Kunde löschen?'
        diff = '';
      }
      msg && this.askDeductableCustomerQuestion(msg, diff);
    }

    //Customer
    if (2 === this.data.deductible_type) {
      if (dv >= 0) {
        diff = tryNoDecimals(dv);

        msg = 'Möchten Sie den SB-Betrag Kunde ' + diff + '€ setzen?'

        assign && this.askDeductableCustomerQuestion(msg, diff);
      } else {
        if (cv > 0) {
          msg = 'Möchten Sie den SB-Betrag Kunde löschen?'
          this.askDeductableCustomerQuestion(msg, '');
        }
      }
    }
  }

  askDeductableCustomerQuestion(msg: string, money: string) {
    const action = (value: any, closeCallback: () => void) => {
      if (value === 'ok') {
        this.setDeductibleCustomer(money);
      }
      isFunction(closeCallback) && closeCallback();
    };

    msg = msg || 'Möchten Sie SB-Betrag setzen?';

    this.notificationService.askQuestion(
      'SB-Betrag Kunde?',
      [msg],
      [
        { value: 'cancel', label: 'Nein' },
        { value: 'ok', label: 'Ja', cls: 'light-bg-color' },
      ],
      action
    );
  }

  setDeductibleCustomer(value: string | number) {
    this.data.deductible_customer_part = value;
  }

  ngOnDestroy(): void {
    this.clearElSub();
    this.unsubAll();
  }

  onLocationChange($event: MatRadioChange) {
    this.montage_loctype = $event.value;
  }

  clickCancel() {
    this.cancelClicked = true;
    this.navService.goBack();
  }

  addCustomer() {
    const sub: any = {
      sub: null,
    };

    sub.sub = this.customerService
      .openNewCustomerDialog(null)
      .subscribe((res: any) => {
        if (isJsObject(res) && res?.id) {
          if (!res.pid && res.partner?.id) {
            res.pid = res.partner.id;
          }
          this.customer = res;

          if (this._customerRef) {
            this._customerRef.addEntry(res);
          }

          this.onCustomerChange(this.customer);
        }

        sub.sub = unsub(sub.sub);
      });
  }

  getData() {

    if (!this.id) {
      return;
    }
    this.canSave = false;
    const url = this.backendUrl();

    // Keine Ahnung warum es aufgerufen wird nach dem Ausloggen.
    if (!this.stateService.isLoggedId) {
      return;
    }

    this.dataRequestSub = this.dataService.get(url).subscribe((data: any) => {
      this.dataRequestSub = null;

      const result = data?.result;

      if (result?.success) {
        const content = result?.data as OrderType;

        if (isJsObject(content)) {
          this.setCanFillOrDelete(!!this.isAdmin || content.status === 1);

          this.customer = content.customer;
          this.partner = content.partner;

          this.canSave = !!content.can_save && !content.deleted;
          this.canRestore = !!content.can_restore;

          const statusId = content.status;

          this.isCompleted = statusId === 2;

          if (content.team && content.team?.id) {
            if (!content.team.name) {
              content.team.name =
                content.team.first_name + ' ' + content.team.last_name;
            }
            if (typeof content.team.id === 'string') {
              content.team.id = parseDec(content.team.id);
            }

            if (content.team.id) {
              this.teamId = content.team.id;
            }

            //this.teamFC.setValue(content.team);
            this.team = content.team;
          }
          if (content.deductible) {
            content.deductible = floatToShortString(content.deductible);
          }

          if (content.deductible_customer_part) {
            let val = parseFloat('' + content.deductible_customer_part);
            if (val > 0) {
              this.statusTip =
                'SB ' + val.toFixed(2) + '&euro; vor Ort kassieren';
              this.needStatus = true;
            }
          }

          content.work_from = setNumberToTimeIfInteger(content.work_from);

          content.work_to = setNumberToTimeIfInteger(content.work_to);

          setDateFields(content, ['damage_day', 'first_reg', 'work_day']);

          this.dataLoaded = true;

          this.data = content;
          return;
        }
      }
      const errs = firstArray(result?.errors, ['Auftragsdaten konnten nicht geladen werden']);
      this.showErrors(errs);
    });
  }

  onTimeChange(isStartTime: boolean) {
    const res = tryParseTime(isStartTime ? this.data.work_from : this.data.work_to);

    if (res !== null) {
      if (isStartTime) {
        this.data.work_from = res;
      } else {
        this.data.work_to = res;
      }
    }
  }

  onWorktimeChange($event: any) {
    this.timesValue = $event;
  }

  openCalendar() {
    if (this.teamId) {
      const sub = new Subject<any>();

      const dialogSub: any = {
        sub: null,
      };

      dialogSub.sub = sub.subscribe((calResponse: any) => {
        dialogSub.sub = unsub(dialogSub.sub);

        if (calResponse) {
          const sameIds = this.id === calResponse.id;

          if (calResponse.deleteDate) {
            const deleteValues = !!calResponse.noOrder || (!!this.id && sameIds);

            if (deleteValues) {
              this.data.work_day = '';
              this.data.work_from = '';
              this.data.work_to = '';
            }

            return;
          }

          const copyValues = !!this.id && sameIds || !!calResponse.newDate;

          if (
            copyValues &&
            calResponse.work_day &&
            Number.isFinite(calResponse.work_from) &&
            Number.isFinite(calResponse.work_to)
          ) {
            this.data.work_day = calResponse.work_day;
            this.data.work_from = numberToTime(calResponse.work_from);
            this.data.work_to = numberToTime(calResponse.work_to);
          }
        }
      });

      this.teamService
        .openPlanDialog(this.team, this.data, sub)
        .subscribe((res: any) => {
          dialogSub.sub = unsub(dialogSub.sub);
        });
    } else {
      this.showErrors(['Bitte zuerst Team auswählen']);
    }
  }

  onCustomerChange(customer: any) {
    if (entryHasId(customer, true)) {
      this.data.customer_id = customer.id;

      if (this._partnerRef) {
        const res = this._partnerRef.setById(customer.pid);
        if (!res) {
          this.showErrors(['Partner für Kunde nicht gefunden']);
        }
      }
    } else {
      delete this.data.customer_id;
    }
  }

  onPartnerChange(partner: any) {
    if (entryHasId(partner, true)) {
      this.data.partner_id = partner.id;
    } else {
      delete this.data.partner_id;
    }
  }

  onDedStatusChange($event: MatRadioChange) {
    this.data.deductible_customer_status = $event.value;
  }

  showValidationErrors(errors: ValidationError[]) {
    const refs = this.getAllRefs();

    const errorMessages: string[] = [];
    const errorElems = new ErrorElemContainer();

    for (let e of errors) {
      if (refs.hasOwnProperty(e.name) && refs[e.name]) {
        errorElems.add(refs[e.name]);
      }
      errorMessages.push(e.error);
    }

    errorElems.highlightErrors();
    this.showErrors(errorMessages);
  }

  handleSaveResponse(response: any, callback?: (res: any) => void) {
    const result = response?.result;

    if (result?.success) {
      this.showSuccess(result.msg || 'Auftragsdaten gespeichert');

      if (result.data) {
        this.id = result.data.id;
      }
      callFuncWith(callback, true);
      return;
    }

    callFuncWith(callback, false);

    const errors = firstArray(result.errors, unknownSaveErrors);

    this.showErrors(errors);
  }

  closeOrder() {
    const dataParams: any = {
      orderId: this.id,
      deductible_customer_status: this.data?.deductible_customer_status,
    };
    subClosed(
      this.matDialog.open(CloseOrderDialogComponent, {
        data: dataParams,
      }),
      (res: any) => {
        res && this.getData();
      }
    );
  }

  clickSave() {
    this.trySave();
  }

  clickRestore() {
    this.orderDialogService.tryRestoreOrder(this.id, (result: any) => {
      if (result && result.success) {
        this.getData();
      }
    });
  }

  isSaving = false;

  /*
  saveSub: any = {
    sub: null
  };
  */

  trySave(callback?: (res: any) => void) {
    if (this.isSaving) {
      console.log('Ignore: Is Saving.', this.isSaving);
      return;
    }
    this.isSaving = true;

    const dataHandler = OrderDataHandler.create(this.dataService);

    const params: OrderSaveParams = {
      data: this.data,
      montage_loctype: this.montage_loctype,
      teamId: this.teamId,
      url: this.backendUrl(),
    };

    const that = this;

    const savingComplete = () => {
      setTimeout(() => {
        that.isSaving = false;
      }, 200);
    };

    /*
    const forgetSub = () => {
      console.log('SASU<', that.saveSub);
      that.saveSub.sub = unsub(that.saveSub.sub, null);
      console.log('SASU>', that.saveSub);
    };
    */

    const sub = dataHandler
      .save(params, this)
      .subscribe(
        {
          complete: () => {
            console.log('Complete!');
            //forgetSub();
            savingComplete();
          },
          next: (res: OrderSaveResult) => {
            try {
              if (res.validationErrors.length > 0) {
                callFuncWith(callback, false);
                this.showValidationErrors(res.validationErrors);
              } else {
                this.handleSaveResponse(res.serverResult, callback);
              }
            } catch (e) {
              console.error('Save error', e);
            }
            //forgetSub();
            savingComplete();
          },
          error: () => {
            //forgetSub();
            savingComplete();
            this.showErrors([
              'Fehler beim Speichern.'
            ]);
          }
        }
      );
  }

  tabSelectedIdx(index: number) {
    this.tabIdx = index;
    setTimeout(() => {
      this.updateHeights(index);
    }, 250);
    // Nur für Montage-Teams neuladen.
    index === 0 && !this.isAdmin && this.getData();
    //index && this.reloadDocs(index);
  }

  tabSelected($event: MatTabChangeEvent) {
    //this.tabIdx = $event.index;
    this.tabSelectedIdx($event.index);
  }

  showSuccess(msg: any) {
    this.notificationService.showSuccess(msg);
  }

  showErrors(errs: any[]) {
    this.notificationService.showErrors(errs);
  }

  showChangeErrorFromResult(result: any) {
    const err = result?.error || 'Fehler bei Änderung';
    this.showErrors([err]);
  }

  trySaveOnClose():
    | boolean
    | UrlTree
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree> {
    if (!this.isAdmin || this.cancelClicked || !this.needAutoSave()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve: (value: any) => void) => {
      try {
        this.autoSaving = true;
        this.trySave((res: any) => {
          this.autoSaving = false;
          resolve(!!res);
        });
      } catch (e) {
        console.error('ERR:', e);
      }
    });
  }
}
